/**
 * EmailLogsPanel - Tableau de bord des emails envoyés
 * Permet de visualiser tous les emails, filtrer et voir les statistiques
 */

import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../utils/apiConfig';

const EmailLogsPanel = ({ token, dateRange }) => {
  const [loading, setLoading] = useState(true);
  const [emails, setEmails] = useState([]);
  const [stats, setStats] = useState(null);
  const [dailyStats, setDailyStats] = useState([]);
  const [triggerStats, setTriggerStats] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    recipient: '',
    subject: '',
    status: '',
    emailType: ''
  });
  const [activeTab, setActiveTab] = useState('logs');

  const loadEmails = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        startDate: dateRange.start,
        endDate: dateRange.end,
        page: page.toString(),
        limit: '50'
      });

      if (filters.recipient) params.append('recipient', filters.recipient);
      if (filters.subject) params.append('subject', filters.subject);
      if (filters.status) params.append('status', filters.status);
      if (filters.emailType) params.append('emailType', filters.emailType);

      const response = await fetch(`${API_BASE_URL}/api/analytics/dashboard/emails?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        setEmails(data.data.emails);
        setStats(data.data.stats);
        setTotalPages(data.data.totalPages);
        setTotal(data.data.total);
      }
    } catch (err) {
      console.error('Erreur chargement emails:', err);
    } finally {
      setLoading(false);
    }
  }, [token, dateRange, page, filters]);

  const loadDetailedStats = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        startDate: dateRange.start,
        endDate: dateRange.end
      });

      const response = await fetch(`${API_BASE_URL}/api/analytics/dashboard/emails/stats?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        setDailyStats(data.data.dailyStats || []);
        setTriggerStats(data.data.triggerStats || []);
      }
    } catch (err) {
      console.error('Erreur chargement stats emails:', err);
    }
  }, [token, dateRange]);

  useEffect(() => {
    loadEmails();
    loadDetailedStats();
  }, [loadEmails, loadDetailedStats]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      sent: { bg: '#e8f5e9', color: '#2e7d32', label: 'Envoye' },
      failed: { bg: '#ffebee', color: '#c62828', label: 'Echec' },
      skipped: { bg: '#fff3e0', color: '#f57c00', label: 'Ignore' },
      disabled: { bg: '#f5f5f5', color: '#666', label: 'Desactive' }
    };
    const config = statusConfig[status] || { bg: '#f5f5f5', color: '#666', label: status };
    return (
      <span style={{
        padding: '3px 8px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: '500',
        background: config.bg,
        color: config.color
      }}>
        {config.label}
      </span>
    );
  };

  const getTypeBadge = (emailType) => {
    const isAuto = emailType === 'auto';
    return (
      <span style={{
        padding: '3px 8px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: '500',
        background: isAuto ? '#e3f2fd' : '#f3e5f5',
        color: isAuto ? '#1565c0' : '#7b1fa2'
      }}>
        {isAuto ? 'Auto' : 'Manuel'}
      </span>
    );
  };

  const getTriggerLabel = (trigger) => {
    const labels = {
      user_creation: 'Création utilisateur',
      account_setup: 'Activation compte',
      password_changed: 'Mot de passe défini',
      password_reset: 'Reset mot de passe',
      notification: 'Notification',
      prestation_assignment: 'Affectation prestation',
      prestation_reminder: 'Rappel prestation',
      prestation_new: 'Nouvelle prestation',
      prestation_update: 'Modification prestation',
      expense_rejected: 'Frais rejeté',
      expense_approved: 'Frais validé',
      user_welcome: 'Bienvenue',
      welcome: 'Bienvenue',
      reminder: 'Rappel',
      manual: 'Envoi manuel',
      unknown: 'Autre'
    };
    return labels[trigger] || trigger || '-';
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({ recipient: '', subject: '', status: '', emailType: '' });
    setPage(1);
  };

  return (
    <div>
      {/* Stats cards */}
      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: '16px',
          marginBottom: '24px'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #667eea, #764ba2)',
            color: 'white',
            padding: '20px',
            borderRadius: '12px'
          }}>
            <div style={{ fontSize: '13px', opacity: 0.9 }}>Total Emails</div>
            <div style={{ fontSize: '28px', fontWeight: '700' }}>{stats.total}</div>
          </div>
          <div style={{ background: '#e8f5e9', padding: '20px', borderRadius: '12px' }}>
            <div style={{ fontSize: '13px', color: '#2e7d32' }}>Envoyes</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#2e7d32' }}>
              {stats.sent}
            </div>
          </div>
          <div style={{ background: '#ffebee', padding: '20px', borderRadius: '12px' }}>
            <div style={{ fontSize: '13px', color: '#c62828' }}>Echecs</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#c62828' }}>
              {stats.failed}
            </div>
          </div>
          <div style={{ background: '#fff3e0', padding: '20px', borderRadius: '12px' }}>
            <div style={{ fontSize: '13px', color: '#f57c00' }}>Ignores</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#f57c00' }}>
              {stats.skipped}
            </div>
          </div>
          <div style={{ background: '#e3f2fd', padding: '20px', borderRadius: '12px' }}>
            <div style={{ fontSize: '13px', color: '#1565c0' }}>Automatiques</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#1565c0' }}>
              {stats.auto}
            </div>
          </div>
          <div style={{ background: '#f3e5f5', padding: '20px', borderRadius: '12px' }}>
            <div style={{ fontSize: '13px', color: '#7b1fa2' }}>Manuels</div>
            <div style={{ fontSize: '28px', fontWeight: '700', color: '#7b1fa2' }}>
              {stats.manual}
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '20px',
        borderBottom: '1px solid #eee',
        paddingBottom: '12px'
      }}>
        {[
          { id: 'logs', label: 'Tous les emails' },
          { id: 'daily', label: 'Par jour' },
          { id: 'triggers', label: 'Par declencheur' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '8px 16px',
              background: activeTab === tab.id ? '#1a1a2e' : 'transparent',
              color: activeTab === tab.id ? 'white' : '#666',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activeTab === tab.id ? '600' : '400'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content: Email logs */}
      {activeTab === 'logs' && (
        <>
          {/* Filtres */}
          <div style={{
            display: 'flex',
            gap: '12px',
            marginBottom: '16px',
            flexWrap: 'wrap',
            alignItems: 'center',
            background: '#f9fafb',
            padding: '16px',
            borderRadius: '10px'
          }}>
            <input
              type="text"
              value={filters.recipient}
              onChange={(e) => handleFilterChange('recipient', e.target.value)}
              placeholder="Rechercher destinataire..."
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                fontSize: '13px',
                minWidth: '180px'
              }}
            />
            <input
              type="text"
              value={filters.subject}
              onChange={(e) => handleFilterChange('subject', e.target.value)}
              placeholder="Rechercher objet..."
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                fontSize: '13px',
                minWidth: '180px'
              }}
            />
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                fontSize: '13px'
              }}
            >
              <option value="">Tous les statuts</option>
              <option value="sent">Envoye</option>
              <option value="failed">Echec</option>
              <option value="skipped">Ignore</option>
              <option value="disabled">Desactive</option>
            </select>
            <select
              value={filters.emailType}
              onChange={(e) => handleFilterChange('emailType', e.target.value)}
              style={{
                padding: '8px 12px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                fontSize: '13px'
              }}
            >
              <option value="">Tous les types</option>
              <option value="auto">Automatique</option>
              <option value="manual">Manuel</option>
            </select>
            <button
              onClick={resetFilters}
              style={{
                padding: '8px 12px',
                background: '#e5e7eb',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500'
              }}
            >
              Reinitialiser
            </button>
          </div>

          {/* Liste des emails */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              Chargement...
            </div>
          ) : emails.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              Aucun email trouve pour cette periode
            </div>
          ) : (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              overflow: 'hidden',
              border: '1px solid #eee'
            }}>
              <div style={{
                padding: '12px 16px',
                background: '#f9fafb',
                borderBottom: '1px solid #eee',
                fontSize: '13px',
                color: '#666'
              }}>
                {total} email{total > 1 ? 's' : ''} trouve{total > 1 ? 's' : ''}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#666' }}>Date</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#666' }}>Destinataire</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#666' }}>Objet</th>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#666' }}>Declencheur</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#666' }}>Type</th>
                    <th style={{ padding: '12px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#666' }}>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {emails.map((email, idx) => (
                    <tr
                      key={email.id}
                      style={{
                        borderBottom: '1px solid #f0f0f0',
                        background: idx % 2 === 0 ? 'white' : '#fafafa'
                      }}
                    >
                      <td style={{ padding: '12px', fontSize: '13px' }}>
                        {formatDate(email.created_at)}
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px' }}>
                        <div style={{ fontWeight: '500' }}>{email.recipient}</div>
                        {email.user_name && (
                          <div style={{ fontSize: '11px', color: '#999' }}>
                            ({email.user_name})
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px', maxWidth: '250px' }}>
                        <div style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {email.subject}
                        </div>
                        {email.template_name && (
                          <div style={{ fontSize: '11px', color: '#999' }}>
                            Template: {email.template_name}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '12px', fontSize: '13px' }}>
                        <div>{getTriggerLabel(email.trigger_source)}</div>
                        {email.sent_by_name && (
                          <div style={{ fontSize: '11px', color: '#999' }}>
                            par {email.sent_by_name}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        {getTypeBadge(email.email_type)}
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                          {getStatusBadge(email.status)}
                          {email.error_message && (
                            <span
                              title={email.error_message}
                              style={{
                                fontSize: '10px',
                                color: '#c62828',
                                cursor: 'help'
                              }}
                            >
                              (voir erreur)
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '8px',
              marginTop: '20px'
            }}>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{
                  padding: '8px 16px',
                  background: page === 1 ? '#eee' : 'white',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  cursor: page === 1 ? 'not-allowed' : 'pointer'
                }}
              >
                Precedent
              </button>
              <span style={{
                padding: '8px 16px',
                background: '#f5f5f5',
                borderRadius: '6px'
              }}>
                Page {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{
                  padding: '8px 16px',
                  background: page === totalPages ? '#eee' : 'white',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  cursor: page === totalPages ? 'not-allowed' : 'pointer'
                }}
              >
                Suivant
              </button>
            </div>
          )}
        </>
      )}

      {/* Tab content: Daily stats */}
      {activeTab === 'daily' && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          border: '1px solid #eee'
        }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', fontWeight: '600' }}>
            Emails par jour
          </h3>
          {dailyStats.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              Aucune donnee pour cette periode
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px' }}>Date</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px' }}>Total</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px' }}>Envoyes</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px' }}>Echecs</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px' }}>Taux</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyStats.map((day) => {
                    const successRate = day.total > 0 ? Math.round((day.sent / day.total) * 100) : 0;
                    return (
                      <tr key={day.date} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px', fontSize: '13px' }}>
                          {new Date(day.date).toLocaleDateString('fr-FR')}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600' }}>
                          {day.total}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right', fontSize: '13px', color: '#2e7d32' }}>
                          {day.sent}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right', fontSize: '13px', color: '#c62828' }}>
                          {day.failed}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right', fontSize: '13px' }}>
                          <span style={{
                            padding: '2px 6px',
                            borderRadius: '4px',
                            background: successRate >= 90 ? '#e8f5e9' : successRate >= 70 ? '#fff3e0' : '#ffebee',
                            color: successRate >= 90 ? '#2e7d32' : successRate >= 70 ? '#f57c00' : '#c62828'
                          }}>
                            {successRate}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Tab content: Trigger stats */}
      {activeTab === 'triggers' && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          border: '1px solid #eee'
        }}>
          <h3 style={{ margin: '0 0 20px 0', fontSize: '16px', fontWeight: '600' }}>
            Emails par declencheur
          </h3>
          {triggerStats.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              Aucune donnee pour cette periode
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    <th style={{ padding: '12px', textAlign: 'left', fontSize: '12px' }}>Declencheur</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px' }}>Total</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px' }}>Envoyes</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px' }}>Echecs</th>
                    <th style={{ padding: '12px', textAlign: 'right', fontSize: '12px' }}>Taux</th>
                  </tr>
                </thead>
                <tbody>
                  {triggerStats.map((trigger) => {
                    const successRate = trigger.total > 0 ? Math.round((trigger.sent / trigger.total) * 100) : 0;
                    return (
                      <tr key={trigger.trigger_source} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px', fontSize: '13px', fontWeight: '500' }}>
                          {getTriggerLabel(trigger.trigger_source)}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600' }}>
                          {trigger.total}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right', fontSize: '13px', color: '#2e7d32' }}>
                          {trigger.sent}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right', fontSize: '13px', color: '#c62828' }}>
                          {trigger.failed}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right', fontSize: '13px' }}>
                          <span style={{
                            padding: '2px 6px',
                            borderRadius: '4px',
                            background: successRate >= 90 ? '#e8f5e9' : successRate >= 70 ? '#fff3e0' : '#ffebee',
                            color: successRate >= 90 ? '#2e7d32' : successRate >= 70 ? '#f57c00' : '#c62828'
                          }}>
                            {successRate}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmailLogsPanel;
